import Vue from 'vue'
import ElementUI from 'element-ui'
import enLocale from 'element-ui/lib/locale/lang/en'
import 'element-ui/lib/theme-chalk/index.css'
import TopBar from '@/components/common/TopBar.vue'
import api from '@/api/api'
import { getToken } from '@/util/token-util'
import { loadHistory, saveHistory } from '@/util/search-history'

Vue.use(ElementUI, { locale: enLocale })
Vue.prototype.$api = api
const paths = { home: '/', cart: '/cart', orders: '/orders', inquiries: '/inquiries', 'account-settings': '/account-settings' }
const pathFor = target => typeof target === 'string' ? target : (paths[target.name] || '/')
Vue.prototype.$router = { push: target => { window.location.href = pathFor(target) } }
Vue.component('router-link', {
  functional: true,
  props: ['to'],
  render(h, context) {
    return h('a', { ...context.data, attrs: { ...context.data.attrs, href: pathFor(context.props.to) } }, context.children)
  }
})
const account = new Vue({ render: h => h(TopBar) }).$mount('#public-account')

let messageTimer
function message(text, type = 'success') {
  account.$message({ message: text, type })
}
async function request(options) {
  const result = await api(options)
  if (!result || result.code !== 200) throw new Error((result && (result.message || result.msg)) || 'Request failed')
  return result.data
}
function requireLogin() {
  if (getToken()) return true
  const topBar = account.$children[0]
  if (topBar) topBar.openLogin()
  message('Please log in first', 'warning')
  return false
}

const catalogue = document.querySelector('[data-catalogue]')
if (catalogue) {
  const form = catalogue.querySelector('[data-catalogue-search]')
  const select = form.querySelector('[data-brand-select]')
  const history = catalogue.querySelector('[data-search-history]')
  const keyword = form.elements.keyword
  const brandKey = catalogue.dataset.brandId ? `brand:${catalogue.dataset.brandId}` : 'all'
  const groups = loadHistory()
  const drawHistory = () => {
    const list = history.querySelector('ul')
    list.replaceChildren()
    const entries = groups[brandKey] || []
    history.hidden = !entries.length
    entries.forEach(entry => {
      const li = document.createElement('li')
      const button = document.createElement('button')
      button.type = 'button'
      button.textContent = entry
      button.addEventListener('click', () => { keyword.value = entry; form.requestSubmit() })
      li.appendChild(button)
      list.appendChild(li)
    })
  }
  const remember = () => {
    const term = keyword.value.trim()
    if (!term) return
    groups[brandKey] = [term, ...(groups[brandKey] || []).filter(item => item !== term)].slice(0, 10)
    saveHistory(groups)
  }
  remember()
  drawHistory()
  form.addEventListener('submit', remember)
  select.addEventListener('change', () => {
    const url = new URL(select.value, window.location.origin)
    if (keyword.value.trim()) url.searchParams.set('keyword', keyword.value.trim())
    window.location.href = url.pathname + url.search
  })
}

document.querySelectorAll('[data-add-cart]').forEach(button => {
  button.addEventListener('click', async () => {
    if (button.disabled || !requireLogin()) return
    const quantityInput = button.closest('tr')?.querySelector('.quantity')
    const quantity = quantityInput ? Number(quantityInput.value) : 1
    if (!Number.isInteger(quantity) || quantity < 1) { message('Quantity must be a positive whole number', 'warning'); return }
    button.disabled = true
    try {
      await request({ url: 'Cart/add', method: 'post', data: { part_id: button.dataset.addCart, quantity } })
      message('Added to cart')
      account.$emit('cart-updated')
    } catch (error) { message(error.message || 'Failed to add to cart', 'error') }
    finally { button.disabled = false }
  })
})

let countries = []
const countryInput = document.querySelector('[data-country-search]')
if (countryInput) {
  let countryRequest = 0
  const searchCountries = async keyword => {
    const current = ++countryRequest
    try {
      const data = await request({ url: 'Shipping/dhlCountries', data: { page: 1, page_size: 50, keyword } })
      if (current !== countryRequest) return
      countries = data.items || []
      const list = document.getElementById('shipping-countries')
      list.replaceChildren()
      countries.forEach(country => {
        const option = document.createElement('option')
        option.value = `${country.country_name} (${country.country_code})`
        list.appendChild(option)
      })
    } catch (error) { message(error.message, 'error') }
  }
  searchCountries('')
  countryInput.addEventListener('input', () => {
    clearTimeout(messageTimer)
    const match = countries.find(country => `${country.country_name} (${country.country_code})` === countryInput.value)
    countryInput.form.elements.country_code.value = match?.country_code || ''
    if (!match) messageTimer = setTimeout(() => searchCountries(countryInput.value.trim()), 300)
  })
}

document.querySelectorAll('[data-api-form]').forEach(form => {
  form.addEventListener('submit', async event => {
    event.preventDefault()
    const action = form.dataset.apiForm
    if (action === 'Inquiry/submit' && !requireLogin()) return
    const button = form.querySelector('[type=submit]')
    if (button.disabled) return
    const data = Object.fromEntries(new FormData(form).entries())
    if (action === 'Shipping/dhlQuote') {
      if (!data.country_code) { message('Please select a destination country from the list', 'warning'); return }
      delete data.country_search
    }
    button.disabled = true
    try {
      const response = await request({ url: action, method: 'post', data })
      if (action === 'Shipping/dhlQuote') {
        const results = document.querySelector('[data-shipping-results]')
        results.replaceChildren()
        const heading = document.createElement('h2')
        heading.textContent = 'Estimated Shipping Cost'
        results.appendChild(heading)
        ;(response.products || []).forEach(product => {
          const option = document.createElement('article')
          option.className = 'quote-option'
          const title = document.createElement('h3')
          title.textContent = `${product.product_name}: USD ${product.usd_total_price ?? '-'}`
          const detail = document.createElement('p')
          detail.textContent = [product.total_transit_days != null ? `Transit: ${product.total_transit_days} day(s)` : '', product.delivery_date ? `Delivery: ${product.delivery_date}` : ''].filter(Boolean).join(' · ')
          option.append(title, detail)
          results.appendChild(option)
        })
        const note = document.createElement('p')
        note.textContent = response.note || (!(response.products || []).length ? 'No shipping options returned.' : 'The final freight will be confirmed after warehouse weighing.')
        results.appendChild(note)
      } else {
        form.reset()
        message(action === 'Inquiry/submit' ? 'Inquiry submitted' : 'Your message has been submitted successfully')
      }
    } catch (error) {
      if (action === 'Shipping/dhlQuote') document.querySelector('[data-shipping-results]').replaceChildren()
      message(error.message || 'Request failed', 'error')
    } finally { button.disabled = false }
  })
})
